import React from 'react';
import { SEO, Button } from '../components/ui';
import './EInvoicingPage.css';

const EInvoicingPage: React.FC = () => {
  return (
    <>
      <SEO
        title="E-Invoicing Technology | Relique Consultants"
        description="GCC e-invoicing advisory and technology brief. Understand the five-corner model, market timelines, and what to require from an Accredited Service Provider."
        canonical="/"
      />

      <div className="e-invoicing-page">
        {/* <div className="lp-section-nav">
          <div className="wrap lp-section-nav-inner">
            <a href="#architecture">How it works</a>
            <a href="#markets">Markets</a>
            <a href="#asp">What an ASP does</a>
            <a href="#contact">Contact</a>
          </div>
        </div> */}

        <header className="hero">
          <div className="wrap hero-inner">
            <div className="eyebrow">
              <span className="dot" />
              GCC E-INVOICING ADVISORY · TECHNOLOGY BRIEF
            </div>
            <h1>
              The technology
              <br />
              behind the <span className="hl">mandate.</span>
            </h1>
            <p className="sub">
              E-invoicing across the Gulf isn&apos;t a PDF rule — it&apos;s a structured data network with defined corners, formats, and reporting paths. Here&apos;s exactly how it works, market by market, and what to require from any Accredited Service Provider you choose.
            </p>
            <div className="cta-row">
              <Button
                to="/contact"
                variant="primary"
                className="!bg-[#7B4EFF] !text-[#F5F5F5] hover:!bg-[#6A42E6] hover:!text-[#F5F5F5] shadow-lg hover:shadow-xl"
              >
                Book a readiness call
              </Button>
              <a href="#architecture" className="btn-secondary">See the architecture ↓</a>
            </div>
            <div className="status-row">
              <div className="status-item">
                <span className="pip live" />
                UAE — pilot live, mandate from Jan 2027
              </div>
              <div className="status-item">
                <span className="pip live" />
                Oman — pilot live since Aug 2026
              </div>
              <div className="status-item">
                <span className="pip pending" />
                Qatar — law approved, mandate expected 2027
              </div>
            </div>
          </div>
        </header>

        <section className="arch" id="architecture">
          <div className="wrap">
            <div className="eyebrow">
              <span className="dot" />
              ARCHITECTURE
            </div>
            <h2>The five-corner model, plainly.</h2>
            <p className="lede">
              Both the UAE and Oman have built their mandates on the same backbone: a decentralised, Peppol-based Continuous Transaction Control (CTC) network. Invoices move directly between businesses over an accredited network — a copy of the same data reaches the tax authority in parallel, not as a separate filing.
            </p>

            <div className="diagram-desktop">
              <svg viewBox="0 0 1120 300" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="#4EC6C6" />
                  </marker>
                </defs>

                <line x1="150" y1="80" x2="330" y2="80" stroke="rgba(123,78,255,0.5)" strokeWidth="1.6" markerEnd="url(#arrow)" />
                <line x1="470" y1="80" x2="650" y2="80" stroke="rgba(123,78,255,0.5)" strokeWidth="1.6" strokeDasharray="2 6" markerEnd="url(#arrow)" />
                <line x1="790" y1="80" x2="970" y2="80" stroke="rgba(123,78,255,0.5)" strokeWidth="1.6" markerEnd="url(#arrow)" />

                <path d="M 400 110 L 400 190 L 530 190" stroke="rgba(123,78,255,0.5)" strokeWidth="1.4" strokeDasharray="2 6" fill="none" markerEnd="url(#arrow)" />
                <path d="M 720 110 L 720 190 L 590 190" stroke="rgba(123,78,255,0.5)" strokeWidth="1.4" strokeDasharray="2 6" fill="none" markerEnd="url(#arrow)" />

                <text x="240" y="66" textAnchor="middle" fontFamily="Roboto, sans-serif" fontSize="10.5" fill="#B0B0B0">ERP export</text>
                <text x="560" y="66" textAnchor="middle" fontFamily="Roboto, sans-serif" fontSize="10.5" fill="#B0B0B0">Peppol AS4 network</text>
                <text x="880" y="66" textAnchor="middle" fontFamily="Roboto, sans-serif" fontSize="10.5" fill="#B0B0B0">Delivery</text>
                <text x="560" y="228" textAnchor="middle" fontFamily="Roboto, sans-serif" fontSize="10.5" fill="#B0B0B0">real-time reporting — parallel, not sequential</text>

                <g transform="translate(30,40)">
                  <rect width="120" height="80" rx="10" fill="#2E2E3E" stroke="rgba(123,78,255,0.4)" />
                  <text x="16" y="26" fontFamily="Roboto, sans-serif" fontSize="11" fontWeight="500" fill="#4EC6C6">C1</text>
                  <text x="16" y="46" fontFamily="Roboto, sans-serif" fontWeight="500" fontSize="13.5" fill="#F5F5F5">Supplier</text>
                  <text x="16" y="64" fontFamily="Roboto, sans-serif" fontSize="9.5" fill="#B0B0B0">Issues from ERP</text>
                </g>

                <g transform="translate(330,40)">
                  <rect width="140" height="80" rx="10" fill="#2E2E3E" stroke="rgba(123,78,255,0.4)" />
                  <text x="16" y="26" fontFamily="Roboto, sans-serif" fontSize="11" fontWeight="500" fill="#4EC6C6">C2</text>
                  <text x="16" y="46" fontFamily="Roboto, sans-serif" fontWeight="500" fontSize="13.5" fill="#F5F5F5">Supplier&apos;s ASP</text>
                  <text x="16" y="64" fontFamily="Roboto, sans-serif" fontSize="9.5" fill="#B0B0B0">Validates &amp; converts</text>
                </g>

                <g transform="translate(650,40)">
                  <rect width="140" height="80" rx="10" fill="#2E2E3E" stroke="rgba(123,78,255,0.4)" />
                  <text x="16" y="26" fontFamily="Roboto, sans-serif" fontSize="11" fontWeight="500" fill="#4EC6C6">C3</text>
                  <text x="16" y="46" fontFamily="Roboto, sans-serif" fontWeight="500" fontSize="13.5" fill="#F5F5F5">Buyer&apos;s ASP</text>
                  <text x="16" y="64" fontFamily="Roboto, sans-serif" fontSize="9.5" fill="#B0B0B0">Receives &amp; checks</text>
                </g>

                <g transform="translate(970,40)">
                  <rect width="120" height="80" rx="10" fill="#2E2E3E" stroke="rgba(123,78,255,0.4)" />
                  <text x="16" y="26" fontFamily="Roboto, sans-serif" fontSize="11" fontWeight="500" fill="#4EC6C6">C4</text>
                  <text x="16" y="46" fontFamily="Roboto, sans-serif" fontWeight="500" fontSize="13.5" fill="#F5F5F5">Buyer</text>
                  <text x="16" y="64" fontFamily="Roboto, sans-serif" fontSize="9.5" fill="#B0B0B0">Posts automatically</text>
                </g>

                <g transform="translate(465,190)">
                  <rect width="150" height="70" rx="10" fill="#553D83" stroke="#4EC6C6" strokeWidth="1.3" />
                  <text x="16" y="24" fontFamily="Roboto, sans-serif" fontSize="11" fontWeight="500" fill="#4EC6C6">C5</text>
                  <text x="16" y="43" fontFamily="Roboto, sans-serif" fontWeight="500" fontSize="13" fill="#F5F5F5">Tax Authority</text>
                  <text x="16" y="59" fontFamily="Roboto, sans-serif" fontSize="9" fill="#B0B0B0">Continuous oversight</text>
                </g>
              </svg>
            </div>

            <div className="diagram-mobile">
              <div className="dm-node">
                <span className="dm-num">C1</span>
                <div>
                  <div className="dm-role">Supplier</div>
                  <div className="dm-desc">Issues the invoice from their ERP or accounting system.</div>
                </div>
              </div>
              <div className="dm-node">
                <span className="dm-num">C2</span>
                <div>
                  <div className="dm-role">Supplier&apos;s ASP</div>
                  <div className="dm-desc">Validates the data and converts it into the required structured format (e.g. PINT AE, PINT OM).</div>
                </div>
              </div>
              <div className="dm-node">
                <span className="dm-num">C3</span>
                <div>
                  <div className="dm-role">Buyer&apos;s ASP</div>
                  <div className="dm-desc">Receives the invoice over the accredited network and checks it before delivery.</div>
                </div>
              </div>
              <div className="dm-node">
                <span className="dm-num">C4</span>
                <div>
                  <div className="dm-role">Buyer</div>
                  <div className="dm-desc">Gets the invoice in a format their own system can post automatically.</div>
                </div>
              </div>
              <div className="dm-node">
                <span className="dm-num">C5</span>
                <div>
                  <div className="dm-role">Tax Authority</div>
                  <div className="dm-desc">Receives a parallel, near real-time report of the same transaction — not a separate filing.</div>
                </div>
              </div>
            </div>

            <div className="arch-caption">
              <strong>Your business only ever touches Corner 1.</strong> Everything from Corner 2 onward — validation, format conversion, network transmission, and tax reporting — is your ASP&apos;s job. Choosing the right one is the decision that actually matters.
            </div>
          </div>
        </section>

        <section className="markets" id="markets">
          <div className="wrap">
            <div className="eyebrow">
              <span className="dot" />
              MARKETS
            </div>
            <h2>Three mandates, three timelines.</h2>
            <p className="lede">
              Same underlying architecture, different clocks. Here&apos;s where each market stands, as of August 2026.
            </p>

            <div className="market-grid">
              <div className="m-card uae">
                <div className="m-head">
                  <span className="m-country">UAE</span>
                  <span className="m-authority">Ministry of Finance<br />&amp; FTA</span>
                </div>
                <div className="m-fields">
                  <div className="m-field">
                    <div className="m-label">Model</div>
                    <div className="m-value">Peppol 5-corner (DCTCE)</div>
                  </div>
                  <div className="m-field">
                    <div className="m-label">Format</div>
                    <div className="m-value">PINT AE — UBL XML</div>
                  </div>
                  <div className="m-field">
                    <div className="m-label">Key dates</div>
                    <div className="m-value">
                      ASP by 30 Oct 2026 (≥AED 50M) → live 1 Jan 2027
                      <br />
                      ASP by 31 Mar 2027 (rest) → live 1 Jul 2027
                    </div>
                  </div>
                </div>
                <div className="m-status">
                  <span className="pulse" />
                  Voluntary pilot live since 1 Jul 2026
                </div>
              </div>

              <div className="m-card qatar">
                <div className="m-head">
                  <span className="m-country">Qatar</span>
                  <span className="m-authority">General Tax<br />Authority</span>
                </div>
                <div className="m-fields">
                  <div className="m-field">
                    <div className="m-label">Model</div>
                    <div className="m-value">Not yet published</div>
                  </div>
                  <div className="m-field">
                    <div className="m-label">Format</div>
                    <div className="m-value">Not yet published</div>
                  </div>
                  <div className="m-field">
                    <div className="m-label">Key dates</div>
                    <div className="m-value">
                      Draft e-invoicing law approved May 2026
                      <br />
                      Phased mandate expected from 2027
                    </div>
                  </div>
                </div>
                <div className="m-status">
                  <span className="pulse" />
                  Technical framework pending
                </div>
              </div>

              <div className="m-card oman">
                <div className="m-head">
                  <span className="m-country">Oman</span>
                  <span className="m-authority">OTA · Fawtara</span>
                </div>
                <div className="m-fields">
                  <div className="m-field">
                    <div className="m-label">Model</div>
                    <div className="m-value">Peppol 5-corner</div>
                  </div>
                  <div className="m-field">
                    <div className="m-label">Format</div>
                    <div className="m-value">PINT OM — UBL 2.1 XML / PDF/A-3</div>
                  </div>
                  <div className="m-field">
                    <div className="m-label">Key dates</div>
                    <div className="m-value">
                      Pilot: large taxpayers, live since Aug 2026
                      <br />
                      Phased rollout continues through 2027–2028
                    </div>
                  </div>
                </div>
                <div className="m-status">
                  <span className="pulse" />
                  Pilot phase in progress
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="asp" id="asp">
          <div className="wrap">
            <div className="eyebrow">
              <span className="dot" />
              VENDOR DILIGENCE
            </div>
            <h2>What you&apos;re actually buying from an ASP.</h2>
            <p className="lede">
              An Accredited Service Provider isn&apos;t a plugin — it&apos;s the entity performing Corners 2 and 3 on your behalf. This is the sequence it runs for every invoice.
            </p>

            <div className="step-row">
              <div className="step">
                <div className="step-num">01</div>
                <h3>Connect</h3>
                <p>Pulls invoice data from your ERP or accounting system via API, file feed, or middleware.</p>
              </div>
              <div className="step">
                <div className="step-num">02</div>
                <h3>Validate &amp; map</h3>
                <p>Checks the data against the local schema and converts it into the mandatory structured format.</p>
              </div>
              <div className="step">
                <div className="step-num">03</div>
                <h3>Transmit</h3>
                <p>Sends the invoice across the network to the buyer&apos;s ASP, and reports the transaction to the tax authority in parallel.</p>
              </div>
              <div className="step">
                <div className="step-num">04</div>
                <h3>Report &amp; archive</h3>
                <p>Keeps the compliant record on file for the statutory retention period and produces an audit trail on request.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="why">
          <div className="wrap">
            <div className="eyebrow">
              <span className="dot" />
              WHY RELIQUE
            </div>
            <h2>One advisory. Three regulators.</h2>
            <div className="why-grid">
              <div className="why-card">
                <h3>Vendor-agnostic</h3>
                <p>We&apos;re not tied to a single ASP, so the recommendation fits your ERP and risk appetite — not a referral arrangement.</p>
              </div>
              <div className="why-card">
                <h3>Cross-market</h3>
                <p>One team tracking FTA, GTA, and OTA guidance as it&apos;s published, so you&apos;re not reconciling three regulators&apos; documentation yourself.</p>
              </div>
              <div className="why-card">
                <h3>Through go-live, not just the report</h3>
                <p>We stay through ERP mapping and ASP onboarding — not just a readiness assessment that sits in your inbox.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact">
          <div className="cta-band">
            <div>
              <h2>Not sure which corner you&apos;re missing?</h2>
              <p>A 30-minute call covers where your business sits against the UAE, Qatar, or Oman timeline — and what to do about it first.</p>
            </div>
            <Button
              to="/contact"
              variant="primary"
              className="!bg-[#4EC6C6] !text-[#0F071D] hover:!bg-[#3AB5B5] hover:!text-[#0F071D]"
            >
              Book a readiness call
            </Button>
          </div>
        </section>

        <section className="lp-disclaimer">
          <div className="wrap">
            <p>
              Regulatory timelines above reflect published guidance as of August 2026 and may change — confirm current requirements with the FTA, GTA, or OTA before acting. This page is general information, not tax advice.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default EInvoicingPage;
